import type { ParsedUrlQuery } from 'querystring';
import slug from 'slug';

/* Prefer slugify when generating urls from arbitrary text, eg category titles- produces clean human-readable URLs.
 * Prefer encodeURIComponent for query param values - handles &, =, ?, # correctly to parse url.
 */
export const getSlug = (text: string | null | undefined): string => {
  if (!text) return '';
  return slug(text);
};

/**
 * Current path *plus query*, for redirect targets that have to survive a round trip through
 * auth. next/navigation's `usePathname()` drops the query string, which breaks any page whose
 * identity lives there — `/map/build` throws outright without its `origin` param. Falls back
 * during SSR, where the caller's pathname is the best available value.
 */
export function currentRelativeUrl(fallback = '/'): string {
  if (globalThis.location === undefined) return fallback;
  return `${globalThis.location.pathname}${globalThis.location.search}`;
}

/**
 * Request header carrying the originally-requested path *plus query*, set by the proxy. Server
 * components can't see the request URL — a layout only knows its own segment — so a gate that
 * has to send the visitor back where they came from reads this instead of guessing.
 */
export const REQUESTED_PATH_HEADER = 'x-byway-requested-path';

/**
 * Validates a `REQUESTED_PATH_HEADER` value before it's used as a redirect target. Only a
 * same-origin absolute path is allowed: a protocol-relative `//evil.com` would otherwise pass
 * a naive leading-slash check and turn the sign-in round trip into an open redirect.
 */
export function safeInternalPath(value: string | null | undefined, fallback: string): string {
  if (!value?.startsWith('/') || value.startsWith('//') || value.includes('\\')) return fallback;
  return value;
}

export const appendParamsToUrl = (baseUrl: string, query: ParsedUrlQuery) => {
  const allowedParams = ['source', 'campaignId', 'utm_source', 'utm_term', 'utm_medium', 'gclid'];

  const filteredQuery = Object.keys(query)
    .filter((key) => allowedParams.includes(key))
    .reduce((obj, key) => {
      obj[key] = query[key];
      return obj;
    }, {});

  const queryString = new URLSearchParams(filteredQuery).toString();
  const separator = baseUrl.includes('?') ? '&' : '?';
  const fullUrl = queryString ? `${baseUrl}${separator}${queryString}` : baseUrl;

  return fullUrl;
};

export function removeLeadingSlash(path: string): string {
  return path.startsWith('/') ? path.slice(1) : path;
}

export function buildUrlRemovingParams(
  pathname: string,
  searchParams: URLSearchParams | Readonly<URLSearchParams> | string | null | undefined,
  keysToRemove: readonly string[]
): string {
  const base = typeof pathname === 'string' ? pathname : '';
  const params = new URLSearchParams(searchParams ?? '');

  let changed = false;
  keysToRemove.forEach((key) => {
    if (params.has(key)) {
      params.delete(key);
      changed = true;
    }
  });

  if (!changed) return base;

  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

export function mergeUrlParams(currentUrl: string, params: Record<string, unknown>): string {
  const url = new URL(currentUrl);

  Object.entries(params).forEach(([key, value]) => {
    if (value == null) return;
    const str = String(value);
    if (!str.trim()) return;
    url.searchParams.set(key, str);
  });

  return url.toString();
}
